import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';
import { isSigninCallback, modeOf, readOidcSettings, stripAuthParams } from './mode';

/**
 * OIDC authorization code + PKCE via oidc-client-ts. Active only when VITE_OIDC_AUTHORITY is set.
 * Tokens live in memory and sessionStorage (both the user store and the PKCE/state store), never in localStorage.
 */
export const oidcSettings = readOidcSettings(import.meta.env);
export const authMode = modeOf(oidcSettings);

/** Set by sign-out so the next page load shows "Signed out" instead of bouncing straight back to the provider. */
const SIGNED_OUT_KEY = 'idrp.signedOut';

let manager: UserManager | null = null;
/** In-memory copy of the current user, kept fresh by the userLoaded/userUnloaded events. */
let current: User | null = null;

function um(): UserManager {
  if (!oidcSettings) throw new Error('OIDC is not configured (VITE_OIDC_AUTHORITY is empty)');
  if (manager) return manager;
  const origin = window.location.origin;
  manager = new UserManager({
    authority: oidcSettings.authority,
    client_id: oidcSettings.clientId,
    scope: oidcSettings.scope,
    redirect_uri: `${origin}/`,
    // Only used when the provider gives no refresh token. The iframe loads the app, which hands off to signinCallback.
    silent_redirect_uri: `${origin}/`,
    post_logout_redirect_uri: `${origin}/`,
    response_type: 'code',
    automaticSilentRenew: true,
    monitorSession: false,
    loadUserInfo: false,
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
  });
  manager.events.addUserLoaded((u) => { current = u; });
  manager.events.addUserUnloaded(() => { current = null; });
  manager.events.addSilentRenewError((e) => console.warn('oidc silent renew failed', e.message));
  return manager;
}

/** The current OIDC access token, or null. Synchronous: read from memory only. */
export function oidcAccessToken(): string | null {
  return current && !current.expired ? current.access_token : null;
}

let signingIn = false;
/** Leave the page for the provider's login. Remembers where the user was (minus any auth response parameters). */
export function signIn(): void {
  if (signingIn) return;
  signingIn = true;
  try { window.sessionStorage.removeItem(SIGNED_OUT_KEY); } catch { /* storage disabled */ }
  const { pathname, search, hash } = window.location;
  void um().signinRedirect({ state: { returnTo: stripAuthParams(pathname, search, hash) } }).catch((e: Error) => {
    signingIn = false;
    console.error('oidc sign-in redirect failed', e);
  });
}

let renewing: Promise<boolean> | null = null;
/** One silent renew (refresh token, else hidden iframe). Concurrent callers share the same attempt. */
export function renewOnce(): Promise<boolean> {
  renewing ??= um().signinSilent()
    .then((u) => { current = u; return !!u && !u.expired; })
    .catch((e: Error) => { console.warn('oidc silent renew failed', e.message); return false; })
    .finally(() => { renewing = null; });
  return renewing;
}

/** Sign out: end the provider session if discovery advertises an end-session endpoint, otherwise local only. */
export async function signOut(): Promise<void> {
  try { window.sessionStorage.setItem(SIGNED_OUT_KEY, '1'); } catch { /* storage disabled */ }
  const m = um();
  let endSession: string | undefined;
  try { endSession = await m.metadataService.getEndSessionEndpoint(); } catch { endSession = undefined; }
  if (endSession) {
    await m.signoutRedirect();
    return;
  }
  await m.removeUser();
  current = null;
  window.location.replace('/');
}

export type BootResult =
  | { kind: 'signed-in' }
  | { kind: 'redirecting' }
  | { kind: 'signed-out' }
  /** We are the silent-renew iframe. Do not render the app. */
  | { kind: 'iframe' }
  | { kind: 'error'; message: string };

/**
 * Run once before the app renders: finish a redirect callback, reuse a session from sessionStorage, or go to sign-in.
 */
export async function bootOidc(): Promise<BootResult> {
  // PKCE needs Web Crypto (crypto.subtle), which browsers expose only on https pages and localhost.
  if (!window.isSecureContext) {
    return { kind: 'error', message: 'sign-in needs HTTPS (or localhost). Serve this app over https behind the reverse proxy.' };
  }
  const m = um();
  const { search, pathname, hash } = window.location;
  if (isSigninCallback(search)) {
    try {
      const user = await m.signinCallback();
      if (!user) return { kind: 'iframe' };
      current = user;
      const returnTo = (user.state as { returnTo?: string } | undefined)?.returnTo;
      window.history.replaceState(null, '', returnTo?.startsWith('/') ? returnTo : stripAuthParams(pathname, search, hash));
      return { kind: 'signed-in' };
    } catch (e) {
      if (window.parent !== window) return { kind: 'iframe' };
      window.history.replaceState(null, '', stripAuthParams(pathname, search, hash));
      return { kind: 'error', message: (e as Error).message };
    }
  }

  let user = await m.getUser();
  if (user?.expired) user = (await renewOnce()) ? current : null;
  if (user) {
    current = user;
    try { window.sessionStorage.removeItem(SIGNED_OUT_KEY); } catch { /* storage disabled */ }
    return { kind: 'signed-in' };
  }
  let signedOut = false;
  try { signedOut = window.sessionStorage.getItem(SIGNED_OUT_KEY) === '1'; } catch { /* storage disabled */ }
  if (signedOut) return { kind: 'signed-out' };
  signIn();
  return { kind: 'redirecting' };
}
